import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { RefreshCw, AlertCircle, Activity, Users, ChevronRight, ChevronDown, PlayCircle, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import DateRangeFilter from '../../../components/filters/DateRangeFilter';
import {
  formatDateYMD,
  computePresetRange,
  describeRange,
} from '../../../utils/dateRangeHelpers';

const API_BASE = 'https://api.glowytics.com';

const EFFECTIVE_STATUS_BADGE = {
  ACTIVE: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', label: 'Aktif' },
  COMPLETED: { cls: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500', label: 'Tamamlandı' },
  PAUSED: { cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', label: 'Duraklatıldı' },
  CAMPAIGN_PAUSED: { cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', label: 'Kampanya Duraklatıldı' },
  ARCHIVED: { cls: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400', label: 'Arşivlendi' },
  DELETED: { cls: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400', label: 'Silindi' },
  PENDING_REVIEW: { cls: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500', label: 'İncelemede' },
  DISAPPROVED: { cls: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500', label: 'Reddedildi' },
  IN_PROCESS: { cls: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500', label: 'İşleniyor' },
  WITH_ISSUES: { cls: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500', label: 'Sorunlu' },
};

// Bitiş/tamamlanma tarihi geçmiş mi? (Meta stop_time / adset end_time)
const isCompleted = (endTime) => {
  if (!endTime) return false;
  const t = new Date(endTime).getTime();
  return Number.isFinite(t) && t < Date.now();
};

// effectiveStatus ACTIVE görünse bile, bitiş tarihi geçmişse "Tamamlandı" göster.
const statusBadge = (effectiveStatus, endTime) => {
  let s = String(effectiveStatus || '').toUpperCase();
  if (s === 'ACTIVE' && isCompleted(endTime)) s = 'COMPLETED';
  const def = EFFECTIVE_STATUS_BADGE[s] || { cls: 'bg-gray-50 text-gray-600 border-gray-200', dot: 'bg-gray-400', label: s || '—' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full border ${def.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${def.dot}`} />
      {def.label}
    </span>
  );
};

const fmtMoney = (v) => {
  if (v == null || isNaN(Number(v))) return '—';
  return `₺${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
};

const fmtNum = (v) => {
  if (v == null || isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 });
};

const fmtPct = (v) => {
  if (v == null || isNaN(Number(v))) return '—';
  return `${(Number(v) * 100).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}%`;
};

const fmtCpe = (v) => {
  if (v == null || isNaN(Number(v))) return '—';
  return `₺${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;
};

const fmtCpm = fmtCpe;

const sum = (rows, key) => rows.reduce((t, r) => t + (Number(r[key]) || 0), 0);
// Seçili dönemde gerçekten yayınlanmış mı?
const hasDelivery = (c) => (Number(c.spend) || 0) > 0 || (Number(c.impressions) || 0) > 0;
const ratio = (a, b) => (b > 0 ? a / b : null);

// ─────────────────────────────────────────────────────────────────────────────
// Mecra tanımları. Her mecra kendi uçlarını, sütunlarını, gruplarını ve
// özet kartlarını tarif eder; tablo ve reklam kartları ortak bileşenlerdir.
// ─────────────────────────────────────────────────────────────────────────────

// Engagement amaçlı Meta kampanyası mı? (yeni ODAX + eski isimler)
const isMetaEngagementCampaign = (objective) => {
  const o = String(objective || '').toUpperCase();
  if (!o) return false;
  if (o.includes('ENGAGEMENT')) return true; // OUTCOME_ENGAGEMENT, POST_ENGAGEMENT, PAGE_LIKES→...
  return ['PAGE_LIKES', 'EVENT_RESPONSES', 'MESSAGES', 'CONVERSATIONS', 'VIDEO_VIEWS', 'REACH'].includes(o);
};

// TikTok objective_type'ları arasında etkileşim odaklı olanlar.
const isTiktokEngagementCampaign = (objective) => {
  const o = String(objective || '').toUpperCase();
  return o.includes('ENGAGEMENT') || o.includes('VIDEO_VIEWS') || o.includes('REACH');
};

const campaignNameCell = (c) => (
  <div className="min-w-0">
    <div className="font-medium text-gray-900 truncate max-w-[260px]" title={c.name}>{c.name}</div>
    <div className="text-xs text-gray-400">{c.id}</div>
  </div>
);

const PLATFORMS = {
  meta: {
    key: 'meta',
    label: 'Meta',
    apiPath: 'meta-ads',
    // Meta'da yaratıcı görseli ve metni ayrı bir uçtan (creative-thumbs) gelir.
    usesCreativeThumbs: true,
    note: null,
    columns: [
      { key: 'name', label: 'Kampanya', render: campaignNameCell },
      { key: 'status', label: 'Durum', render: (c) => statusBadge(c.effectiveStatus, c.stopTime) },
      { key: 'objective', label: 'Objective', cls: 'text-xs text-gray-600', render: (c) => c.objective || '—' },
      { key: 'dailyBudget', label: 'Günlük Bütçe', align: 'right', render: (c) => fmtMoney(c.dailyBudget) },
      { key: 'lifetimeBudget', label: 'Lifetime', align: 'right', render: (c) => fmtMoney(c.lifetimeBudget) },
      { key: 'spend', label: 'Spend', align: 'right', cls: 'font-medium', render: (c) => fmtMoney(c.spend) },
      { key: 'reach', label: 'Reach', align: 'right', render: (c) => fmtNum(c.reach) },
      { key: 'impressions', label: 'Imp.', align: 'right', render: (c) => fmtNum(c.impressions) },
      { key: 'clicks', label: 'Click', align: 'right', render: (c) => fmtNum(c.clicks) },
      { key: 'ctr', label: 'CTR', align: 'right', render: (c) => fmtPct(c.ctr) },
      { key: 'cpm', label: 'CPM', align: 'right', render: (c) => fmtCpm(c.cpm) },
      { key: 'postEngagement', label: 'Post Eng.', align: 'right', render: (c) => fmtNum(c.postEngagement) },
      { key: 'cpe', label: 'CPE', align: 'right', render: (c) => fmtCpe(c.costPerPostEngagement) },
    ],
    groups: [
      { key: 'engagement', title: 'Engagement Kampanyaları', defaultOpen: true, match: (c) => isMetaEngagementCampaign(c.objective) },
      { key: 'others', title: 'Diğer Kampanyalar', defaultOpen: false, match: (c) => !isMetaEngagementCampaign(c.objective) },
    ],
    summary: (rows) => {
      const spend = sum(rows, 'spend');
      const impressions = sum(rows, 'impressions');
      const engagement = sum(rows, 'postEngagement');
      const active = rows.filter((r) => String(r.effectiveStatus).toUpperCase() === 'ACTIVE' && !isCompleted(r.stopTime)).length;
      return [
        { label: 'Kampanya', value: fmtNum(rows.length), sub: `${active} aktif` },
        { label: 'Harcama', value: fmtMoney(spend) },
        { label: 'Erişim', value: fmtNum(sum(rows, 'reach')) },
        { label: 'Gösterim', value: fmtNum(impressions) },
        { label: 'Post Engagement', value: fmtNum(engagement), icon: <Users size={14} /> },
        { label: 'CPE', value: fmtCpe(ratio(spend, engagement)), sub: `CTR ${fmtPct(ratio(sum(rows, 'clicks'), impressions))}` },
      ];
    },
    adStats: (a) => [
      { label: 'Spend', value: fmtMoney(a.spend), bold: true },
      { label: 'Reach', value: fmtNum(a.reach) },
      { label: 'Imp.', value: fmtNum(a.impressions) },
      { label: 'Click', value: fmtNum(a.clicks) },
      { label: 'CTR', value: fmtPct(a.ctr) },
      { label: 'CPM', value: fmtCpm(a.cpm) },
      { label: 'Post Eng.', value: fmtNum(a.postEngagement) },
      { label: 'CPE', value: fmtCpe(a.costPerPostEngagement) },
    ],
  },

  tiktok: {
    key: 'tiktok',
    label: 'TikTok',
    apiPath: 'tiktok-ads',
    usesCreativeThumbs: false,
    note: null,
    columns: [
      { key: 'name', label: 'Kampanya', render: campaignNameCell },
      { key: 'status', label: 'Durum', render: (c) => statusBadge(c.effectiveStatus, c.stopTime) },
      { key: 'objective', label: 'Amaç', cls: 'text-xs text-gray-600', render: (c) => c.objective || '—' },
      { key: 'dailyBudget', label: 'Günlük Bütçe', align: 'right', render: (c) => fmtMoney(c.dailyBudget) },
      { key: 'lifetimeBudget', label: 'Toplam Bütçe', align: 'right', render: (c) => fmtMoney(c.lifetimeBudget) },
      { key: 'spend', label: 'Spend', align: 'right', cls: 'font-medium', render: (c) => fmtMoney(c.spend) },
      { key: 'reach', label: 'Reach', align: 'right', render: (c) => fmtNum(c.reach) },
      { key: 'impressions', label: 'Imp.', align: 'right', render: (c) => fmtNum(c.impressions) },
      { key: 'clicks', label: 'Click', align: 'right', render: (c) => fmtNum(c.clicks) },
      { key: 'ctr', label: 'CTR', align: 'right', render: (c) => fmtPct(c.ctr) },
      { key: 'cpm', label: 'CPM', align: 'right', render: (c) => fmtCpm(c.cpm) },
      { key: 'videoViews', label: 'İzlenme', align: 'right', render: (c) => fmtNum(c.videoViews) },
      { key: 'engagement', label: 'Etkileşim', align: 'right', render: (c) => fmtNum(c.engagement) },
      { key: 'cpe', label: 'CPE', align: 'right', render: (c) => fmtCpe(c.costPerEngagement) },
    ],
    groups: [
      { key: 'engagement', title: 'Etkileşim Kampanyaları', defaultOpen: true, match: (c) => isTiktokEngagementCampaign(c.objective) },
      { key: 'others', title: 'Diğer Kampanyalar', defaultOpen: false, match: (c) => !isTiktokEngagementCampaign(c.objective) },
    ],
    summary: (rows) => {
      const spend = sum(rows, 'spend');
      const impressions = sum(rows, 'impressions');
      const engagement = sum(rows, 'engagement');
      const active = rows.filter((r) => String(r.effectiveStatus).toUpperCase() === 'ACTIVE' && !isCompleted(r.stopTime)).length;
      return [
        { label: 'Kampanya', value: fmtNum(rows.length), sub: `${active} aktif` },
        { label: 'Harcama', value: fmtMoney(spend) },
        { label: 'Erişim', value: fmtNum(sum(rows, 'reach')) },
        { label: 'İzlenme', value: fmtNum(sum(rows, 'videoViews')) },
        { label: 'Etkileşim', value: fmtNum(engagement), icon: <Users size={14} /> },
        { label: 'CPE', value: fmtCpe(ratio(spend, engagement)), sub: `CTR ${fmtPct(ratio(sum(rows, 'clicks'), impressions))}` },
      ];
    },
    adStats: (a) => [
      { label: 'Spend', value: fmtMoney(a.spend), bold: true },
      { label: 'Reach', value: fmtNum(a.reach) },
      { label: 'Imp.', value: fmtNum(a.impressions) },
      { label: 'İzlenme', value: fmtNum(a.videoViews) },
      { label: 'CTR', value: fmtPct(a.ctr) },
      { label: 'CPM', value: fmtCpm(a.cpm) },
      { label: 'Beğeni', value: fmtNum(a.likes) },
      { label: 'Yorum', value: fmtNum(a.comments) },
      { label: 'Paylaşım', value: fmtNum(a.shares) },
      { label: 'Takip', value: fmtNum(a.follows) },
      { label: 'Etkileşim', value: fmtNum(a.engagement) },
      { label: 'CPE', value: fmtCpe(a.costPerEngagement) },
    ],
  },

  youtube: {
    key: 'youtube',
    label: 'YouTube',
    apiPath: 'google-ads',
    usesCreativeThumbs: false,
    // `video` kaynağı yalnızca video yaratıcısı olan gösterimleri raporlar.
    note: 'Reklam kartları YouTube video yaratıcısı bazındadır; toplamları kampanya satırından düşük kalabilir.',
    columns: [
      { key: 'name', label: 'Kampanya', render: campaignNameCell },
      { key: 'status', label: 'Durum', render: (c) => statusBadge(c.effectiveStatus, c.stopTime) },
      { key: 'objective', label: 'Tür', cls: 'text-xs text-gray-600', render: (c) => c.channelType || '—' },
      { key: 'dailyBudget', label: 'Günlük Bütçe', align: 'right', render: (c) => fmtMoney(c.dailyBudget) },
      { key: 'lifetimeBudget', label: 'Toplam Bütçe', align: 'right', render: (c) => fmtMoney(c.lifetimeBudget) },
      { key: 'spend', label: 'Spend', align: 'right', cls: 'font-medium', render: (c) => fmtMoney(c.spend) },
      { key: 'impressions', label: 'Imp.', align: 'right', render: (c) => fmtNum(c.impressions) },
      { key: 'clicks', label: 'Click', align: 'right', render: (c) => fmtNum(c.clicks) },
      { key: 'ctr', label: 'CTR', align: 'right', render: (c) => fmtPct(c.ctr) },
      { key: 'cpm', label: 'CPM', align: 'right', render: (c) => fmtCpm(c.cpm) },
      { key: 'videoViews', label: 'Görüntüleme', align: 'right', render: (c) => fmtNum(c.videoViews) },
      { key: 'cpv', label: 'CPV', align: 'right', render: (c) => fmtCpe(c.cpv) },
      { key: 'engagement', label: 'Etkileşim', align: 'right', render: (c) => fmtNum(c.engagement) },
      { key: 'cpe', label: 'CPE', align: 'right', render: (c) => fmtCpe(c.costPerEngagement) },
    ],
    // Hesapta yüzlerce eski video kampanyası duruyor; dönemde yayınlanmayanlar
    // ayrı ve kapalı bir gruba düşer.
    groups: [
      { key: 'video', title: 'YouTube Video Kampanyaları', defaultOpen: true, match: (c) => hasDelivery(c) && c.channelType !== 'DEMAND_GEN' },
      { key: 'demandgen', title: 'Demand Gen Kampanyaları', defaultOpen: true, match: (c) => hasDelivery(c) && c.channelType === 'DEMAND_GEN' },
      { key: 'idle', title: 'Bu Dönemde Yayınlanmayanlar', defaultOpen: false, match: () => true },
    ],
    summary: (rows) => {
      const spend = sum(rows, 'spend');
      const impressions = sum(rows, 'impressions');
      const views = sum(rows, 'videoViews');
      const engagement = sum(rows, 'engagement');
      // Hesapta duran eski kampanyalar sayıma girmesin — dönemde yayınlananlar sayılır.
      const delivered = rows.filter(hasDelivery);
      const active = delivered.filter((r) => String(r.effectiveStatus).toUpperCase() === 'ACTIVE' && !isCompleted(r.stopTime)).length;
      return [
        { label: 'Yayınlanan Kampanya', value: fmtNum(delivered.length), sub: `${active} aktif · ${rows.length} toplam` },
        { label: 'Harcama', value: fmtMoney(spend) },
        { label: 'Gösterim', value: fmtNum(impressions) },
        { label: 'Görüntüleme', value: fmtNum(views), sub: `İzlenme oranı ${fmtPct(ratio(views, impressions))}` },
        { label: 'Etkileşim', value: fmtNum(engagement), icon: <Users size={14} /> },
        { label: 'CPV', value: fmtCpe(ratio(spend, views)), sub: `CPE ${fmtCpe(ratio(spend, engagement))}` },
      ];
    },
    adStats: (a) => [
      { label: 'Spend', value: fmtMoney(a.spend), bold: true },
      { label: 'Imp.', value: fmtNum(a.impressions) },
      { label: 'Görüntüleme', value: fmtNum(a.videoViews) },
      { label: 'Click', value: fmtNum(a.clicks) },
      { label: 'CTR', value: fmtPct(a.ctr) },
      { label: 'CPV', value: fmtCpe(a.cpv) },
      { label: 'Etkileşim', value: fmtNum(a.engagement) },
      { label: 'CPE', value: fmtCpe(a.costPerEngagement) },
      { label: 'İzlenme Oranı', value: fmtPct(a.viewRate) },
    ],
  },
};

const PLATFORM_ORDER = ['meta', 'tiktok', 'youtube'];

export default function EngagementMonitor() {
  const { selectedBrand } = useAuth();
  const [platformKey, setPlatformKey] = useState('meta');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const [dateRange, setDateRange] = useState('today');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);

  // Expanded campaigns + ad data + creative-thumb cache (Meta'da gerçek görsel + metin)
  const [expanded, setExpanded] = useState(() => new Set());
  const [adsByCampaign, setAdsByCampaign] = useState({}); // { [campaignId]: { loading, error, ads } }
  const [thumbsCache, setThumbsCache] = useState({}); // { [creativeId]: { image_url, thumbnail_url, body, title, is_video, ... } }

  const platform = PLATFORMS[platformKey];

  // Kampanya gruplarının açık/kapalı durumu — mecra değişince tanımına göre kurulur.
  const [groupOpen, setGroupOpen] = useState(() => {
    const init = {};
    PLATFORMS.meta.groups.forEach((g) => { init[g.key] = g.defaultOpen; });
    return init;
  });
  useEffect(() => {
    const next = {};
    platform.groups.forEach((g) => { next[g.key] = g.defaultOpen; });
    setGroupOpen(next);
  }, [platform]);

  const { startDate, endDate } = useMemo(() => {
    if (dateRange === 'custom') {
      return {
        startDate: customStart ? formatDateYMD(customStart) : null,
        endDate: customEnd ? formatDateYMD(customEnd) : null,
      };
    }
    const r = computePresetRange(dateRange);
    if (!r || !r.startDate || !r.endDate) return { startDate: null, endDate: null };
    return {
      startDate: formatDateYMD(r.startDate),
      endDate: formatDateYMD(r.endDate),
    };
  }, [dateRange, customStart, customEnd]);

  const fetchData = useCallback(async () => {
    if (!selectedBrand?.id || !startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ startDate, endDate });
      const r = await fetch(`${API_BASE}/api/${platform.apiPath}/live/engagement-campaigns?${qs.toString()}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id,
        },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || body.details || `HTTP ${r.status}`);
      }
      const json = await r.json();
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id, startDate, endDate, platform.apiPath]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset expanded state when date range, brand or platform changes
  useEffect(() => {
    setExpanded(new Set());
    setAdsByCampaign({});
  }, [selectedBrand?.id, startDate, endDate, platformKey]);

  const fetchAdsForCampaign = useCallback(async (campaignId) => {
    if (!selectedBrand?.id || !startDate || !endDate) return;
    setAdsByCampaign(prev => ({ ...prev, [campaignId]: { loading: true, ads: [], error: null } }));
    try {
      const qs = new URLSearchParams({ campaignId, startDate, endDate });
      const r = await fetch(`${API_BASE}/api/${platform.apiPath}/live/engagement-campaign-ads?${qs.toString()}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || body.details || `HTTP ${r.status}`);
      }
      const json = await r.json();
      const ads = json.ads || [];
      setAdsByCampaign(prev => ({ ...prev, [campaignId]: { loading: false, ads, error: null } }));

      if (!platform.usesCreativeThumbs) return;
      // Prefetch creative thumbs (real image_url + body/title — avoids FB cookie-consent iframe)
      const creativeIds = ads.map(a => a.creativeId).filter(Boolean);
      if (creativeIds.length) {
        const uncached = creativeIds.filter(id => !thumbsCache[id]);
        if (uncached.length) {
          try {
            const chunk = uncached.slice(0, 30).join(',');
            const tr = await fetch(`${API_BASE}/api/${platform.apiPath}/live/creative-thumbs?creativeIds=${encodeURIComponent(chunk)}`, {
              credentials: 'include',
              headers: { 'X-Brand-ID': selectedBrand.id },
            });
            if (tr.ok) {
              const tj = await tr.json();
              const rows = Array.isArray(tj?.rows) ? tj.rows : [];
              if (rows.length) {
                setThumbsCache(prev => {
                  const next = { ...prev };
                  rows.forEach(row => {
                    if (row?.creative_id) next[row.creative_id] = row;
                  });
                  return next;
                });
              }
            }
          } catch (_) { /* ignore thumb failures, AdCard falls back to ad.thumbnailUrl */ }
        }
      }
    } catch (e) {
      setAdsByCampaign(prev => ({ ...prev, [campaignId]: { loading: false, ads: [], error: e.message } }));
    }
  }, [selectedBrand?.id, startDate, endDate, thumbsCache, platform.apiPath, platform.usesCreativeThumbs]);

  const toggleExpand = useCallback((campaignId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
        // Lazy fetch on first expand
        if (!adsByCampaign[campaignId]) fetchAdsForCampaign(campaignId);
      }
      return next;
    });
  }, [adsByCampaign, fetchAdsForCampaign]);

  const rows = useMemo(() => data?.campaigns || [], [data]);
  const summaryCards = useMemo(() => platform.summary(rows), [platform, rows]);
  // İlk eşleşen grup kazanır — satırlar birden fazla grupta görünmez.
  const groups = useMemo(() => {
    const buckets = platform.groups.map((g) => ({ ...g, rows: [] }));
    rows.forEach((row) => {
      const bucket = buckets.find((g) => g.match(row));
      if (bucket) bucket.rows.push(row);
    });
    return buckets;
  }, [platform, rows]);

  const colCount = platform.columns.length;

  const renderCampaignRow = (c) => {
    const isOpen = expanded.has(c.id);
    const adState = adsByCampaign[c.id];
    return (
      <React.Fragment key={c.id}>
        <tr
          className="border-b border-gray-50 hover:bg-gray-50/40 cursor-pointer"
          onClick={() => toggleExpand(c.id)}
        >
          {platform.columns.map((col, idx) => (
            <td key={col.key} className={`px-3 py-2.5 ${col.align === 'right' ? 'text-right' : ''} ${col.cls || ''}`}>
              {idx === 0 ? (
                <div className="flex items-start gap-1.5">
                  <button
                    type="button"
                    className="mt-0.5 text-gray-400 hover:text-gray-600 shrink-0"
                    onClick={(e) => { e.stopPropagation(); toggleExpand(c.id); }}
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                  >
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  {col.render(c)}
                </div>
              ) : col.render(c)}
            </td>
          ))}
        </tr>
        {isOpen && (
          <tr className="bg-gray-50/30">
            <td colSpan={colCount} className="px-3 py-3">
              <AdsPanel
                state={adState}
                platform={platform}
                thumbsCache={thumbsCache}
                campaignStopTime={c.stopTime}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderGroupHeader = (group) => {
    const open = groupOpen[group.key];
    const spend = sum(group.rows, 'spend');
    return (
      <tr
        className="border-b border-gray-100 bg-gray-100/70 cursor-pointer select-none hover:bg-gray-100"
        onClick={() => setGroupOpen(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
      >
        <td colSpan={colCount} className="px-3 py-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            {group.title}
            <span className="text-xs font-normal text-gray-500">
              · {group.rows.length} kampanya · {fmtMoney(spend)}
            </span>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-[1400px] mx-auto">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Activity size={22} className="text-blue-600" />
            Engagement Monitor (Canlı)
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Meta, TikTok ve YouTube'dan canlı: tüm kampanyaların anlık durumu, bütçesi ve içerikleri.
            {data?.fetchedAt && (
              <> <span className="text-gray-400">· Son çekim: {new Date(data.fetchedAt).toLocaleTimeString('tr-TR')}</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeFilter
            value={dateRange}
            customStart={customStart}
            customEnd={customEnd}
            onChange={(r, s, e) => {
              setDateRange(r);
              setCustomStart(s);
              setCustomEnd(e);
            }}
          />
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Yenile
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200">
        {PLATFORM_ORDER.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setPlatformKey(key)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              key === platformKey
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {PLATFORMS[key].label}
          </button>
        ))}
      </div>

      <div className="text-xs text-gray-400">
        {describeRange(dateRange, customStart, customEnd)}
        {data?.accountId && <> · Hesap: <code>{data.accountId}</code></>}
        {data?.accountName && <> ({data.accountName})</>}
        {data?.totalCampaignsScanned != null && (
          <> · {data.totalCampaignsScanned} kampanya tarandı, {data.matchedCount} eşleşme</>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">{platform.label} verisi çekilemedi</div>
            <div className="text-rose-600">{error}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {summaryCards.map((card) => (
          <SummaryCard key={card.label} {...card} />
        ))}
      </div>

      {platform.note && (
        <div className="text-xs text-gray-400">{platform.note}</div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100 bg-gray-50/60">
                {platform.columns.map((col) => (
                  <th key={col.key} className={`px-3 py-2.5 font-medium ${col.align === 'right' ? 'text-right' : ''}`}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={colCount} className="px-3 py-10 text-center text-gray-400">{platform.label}'dan çekiliyor…</td></tr>
              )}
              {!loading && data?.campaigns?.length === 0 && (
                <tr><td colSpan={colCount} className="px-3 py-10 text-center text-gray-400">Kampanya bulunamadı.</td></tr>
              )}
              {!loading && data && groups.map((group) => (
                group.rows.length > 0 && (
                  <React.Fragment key={group.key}>
                    {renderGroupHeader(group)}
                    {groupOpen[group.key] && group.rows.map(renderCampaignRow)}
                  </React.Fragment>
                )
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, sub, icon }) {
  return (
    <Card className="p-3">
      <div className="text-xs text-gray-500 flex items-center gap-1.5">{icon}{label}</div>
      <div className="mt-1 text-lg font-semibold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </Card>
  );
}

function AdsPanel({ state, platform, thumbsCache, campaignStopTime }) {
  if (!state || state.loading) {
    return <div className="text-sm text-gray-500 py-3 pl-6">Reklamlar {platform.label}'dan çekiliyor…</div>;
  }
  if (state.error) {
    return (
      <div className="flex items-start gap-2 p-2 rounded border border-rose-200 bg-rose-50 text-rose-700 text-xs">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <span>Reklamlar çekilemedi: {state.error}</span>
      </div>
    );
  }
  const ads = state.ads || [];
  if (ads.length === 0) {
    return <div className="text-sm text-gray-500 py-3 pl-6">Bu kampanyada reklam bulunamadı.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 pl-6">
      {ads.map((a) => (
        <AdCard
          key={a.id}
          ad={a}
          platform={platform}
          thumb={platform.usesCreativeThumbs && a.creativeId ? thumbsCache[a.creativeId] : null}
          campaignStopTime={campaignStopTime}
        />
      ))}
    </div>
  );
}

function pickImage(thumb, fallback) {
  if (!thumb) return fallback || null;
  return (
    thumb.best_media_url ||
    thumb.enhanced_image_url ||
    thumb.image_url ||
    thumb.video_thumbnail_url ||
    thumb.thumbnail_url ||
    fallback ||
    null
  );
}

function AdCard({ ad, platform, thumb, campaignStopTime }) {
  const imageUrl = pickImage(thumb, ad.thumbnailUrl);
  // Meta'da video bilgisi creative-thumbs'tan, diğer mecralarda reklamın kendisinden gelir.
  const isVideo = platform.usesCreativeThumbs ? !!thumb?.is_video : ad.type === 'video' || !!ad.videoId;
  const title = (thumb?.title || ad.title || '').trim();
  const body = (thumb?.body || ad.body || '').trim();
  const linkUrl = (thumb?.link_url || ad.landingPageUrl || '').trim();
  const previewUrl = (ad.previewUrl || '').trim();

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm flex flex-col">
      <div className="px-3 py-2 flex items-start justify-between gap-2 border-b border-gray-50">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate" title={ad.name}>{ad.name}</div>
          <div className="text-[10px] text-gray-400">{ad.adId || ad.id}</div>
        </div>
        {statusBadge(ad.effectiveStatus, ad.adsetEndTime || campaignStopTime)}
      </div>

      <div className="relative bg-gray-100 flex justify-center items-center" style={{ minHeight: 240 }}>
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt={ad.name}
              className="w-full object-cover"
              style={{ maxHeight: 320 }}
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            {isVideo && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <PlayCircle size={48} className="text-white drop-shadow-lg opacity-90" />
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-400 py-12 gap-1">
            <ImageIcon size={28} />
            <span className="text-xs">Görsel yok</span>
          </div>
        )}
        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/60 text-white text-[10px] hover:bg-black/75"
          >
            <ExternalLink size={11} />
            İzle
          </a>
        )}
      </div>

      {(title || body) && (
        <div className="px-3 py-2 border-b border-gray-50 text-xs space-y-1">
          {title && <div className="font-medium text-gray-800 line-clamp-2" title={title}>{title}</div>}
          {body && <div className="text-gray-600 line-clamp-3" title={body}>{body}</div>}
          {linkUrl && (
            <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline truncate block" title={linkUrl}>
              {linkUrl}
            </a>
          )}
        </div>
      )}

      <div className="px-3 py-2.5 grid grid-cols-3 gap-y-1.5 gap-x-3 text-xs mt-auto">
        {platform.adStats(ad).map((s) => (
          <Stat key={s.label} label={s.label} value={s.value} bold={s.bold} />
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, bold }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-gray-400">{label}</span>
      <span className={`text-gray-900 ${bold ? 'font-semibold' : ''}`}>{value}</span>
    </div>
  );
}
